/**
 * Post-save warning when a person is only visible in the other app mode.
 */

import * as React from 'react';
import { ConfirmModal } from '@/components/common/modals/ConfirmModal';
import { SUPPORT_TYPE_LABEL } from '@/constants/shared-catalog';
import type { AppMode } from '@/contexts/ModeContext';

/** Cross-mode warning: stay here or switch to the mode where the person is listed. */
type CrossModeAfterSaveModalProps = Readonly<{
  open: boolean;
  targetMode: AppMode;
  /** Domain list name in the copy (`Administration`, `Tuteurs`, …). */
  listLabel: string;
  onStay: () => void;
  onSwitch: () => void;
}>;

/** Args for `renderCrossModeAfterSaveModal`. */
type RenderCrossModeAfterSaveModalParams = Readonly<{
  targetMode: AppMode | null;
  listLabel: string;
  onStay: () => void;
  onSwitch: () => void;
}>;

/** Cross-mode warning, or null when the saved person stays on the current list. */
export function renderCrossModeAfterSaveModal({
  targetMode,
  listLabel,
  onStay,
  onSwitch,
}: RenderCrossModeAfterSaveModalParams): React.ReactNode {
  if (!targetMode) return null;
  return (
    <CrossModeAfterSaveModal
      open
      targetMode={targetMode}
      listLabel={listLabel}
      onStay={onStay}
      onSwitch={onSwitch}
    />
  );
}

/** Warns that the person left the current-mode list. */
function CrossModeAfterSaveModal({
  open,
  targetMode,
  listLabel,
  onStay,
  onSwitch,
}: CrossModeAfterSaveModalProps): React.ReactNode {
  const targetLabel = SUPPORT_TYPE_LABEL[targetMode];
  const currentLabel = SUPPORT_TYPE_LABEL[targetMode === 'remote' ? 'in_person' : 'remote'];
  const description = `Vous ne lui avez attribué que des rôles en ${targetLabel.toLowerCase()}. Il n'apparaîtra plus dans la liste ${listLabel} en ${currentLabel.toLowerCase()}. Passez en mode ${targetLabel.toLowerCase()} pour le retrouver.`;

  return (
    <ConfirmModal
      open={open}
      onClose={onStay}
      onConfirm={onSwitch}
      variant="info"
      title="Cette personne n'apparaîtra plus ici"
      description={description}
      cancelText="OK"
      confirmText={`Voir en ${targetLabel.toLowerCase()}`}
    />
  );
}
